"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { AnimatePresence, motion, useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { buttonVariants, LazyMount, SectionHeader } from "@/components/ui/";
import { Check, Copy, ArrowRight, ChevronRight } from "@/components/icons";
import { DEVELOPERS_URL, HELP_URL } from "@/lib/constants";
import { languages, codeSnippets, features, codeVariants } from "./data";

const CodeDisplay = dynamic(() => import("./code-display"), {
  ssr: false,
  loading: () => (
    <pre
      aria-hidden="true"
      style={{
        margin: 0,
        background: "transparent",
        padding: "1.25rem 1.5rem",
        fontSize: "0.8125rem",
        lineHeight: 1.7,
        color: "color-mix(in srgb, var(--foreground) 50%, transparent)",
        fontFamily: "var(--font-geist-mono, ui-monospace, monospace)",
        whiteSpace: "pre",
      }}
    />
  ),
});

// Filename shown in the window chrome, per language tab.
const FILENAMES: Record<string, string> = {
  python: "proxy.py",
  nodejs: "proxy.mjs",
  go: "main.go",
  php: "proxy.php",
  curl: "terminal",
  java: "ProxidizeExample.java",
  csharp: "Program.cs",
};

export default function ApiSection() {
  const [activeTab, setActiveTab] = useState("python");
  const [copied, setCopied] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const dir = e.key === "ArrowRight" ? 1 : -1;
    const currentIndex = languages.findIndex((lang) => lang.id === activeTab);
    const next = (currentIndex + dir + languages.length) % languages.length;
    setActiveTab(languages[next].id);
    tabRefs.current[next]?.focus();
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(codeSnippets[activeTab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lineCount = codeSnippets[activeTab].split("\n").length;

  return (
    <section
      id="api"
      /* full-bleed so the band spans the viewport rather than stopping at the
         frame hairlines — same treatment as the other tinted bands. */
      className="full-bleed py-section border-border bg-band border-b"
      aria-labelledby="api-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-8 lg:grid-cols-[1fr_1.3fr] lg:gap-16">
          <div>
            <SectionHeader
              eyebrow="Automation & API"
              id="api-heading"
              heading={
                <>
                  Simple API.
                  <br />
                  Integrate in minutes.
                </>
              }
              body="Standard HTTP proxy protocol. No SDK, no custom library. Drop in your credentials and you're live."
              bodyClassName="mb-8"
            />

            <ul className="mb-10 space-y-6" aria-label="API features">
              {features.map((f) => {
                const FIcon = f.icon;
                return (
                  <li key={f.title} className="flex gap-4">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center">
                      <FIcon size={17} className="text-muted-foreground" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-foreground mb-0.5 text-lg font-semibold">{f.title}</h3>
                      <p className="text-muted-foreground text-sm">{f.description}</p>
                    </div>
                  </li>
                );
              })}
            </ul>

            <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
              Need to configure rotation intervals or sticky sessions? See the{" "}
              <Link
                href={HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
              >
                mobile proxy rotation setup guide
              </Link>
              .
            </p>

            <div className="flex flex-wrap gap-3">
              <Link
                href={DEVELOPERS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(buttonVariants({ size: "lg" }), "font-medium hover:opacity-90")}
              >
                Read the docs
                <ArrowRight size={14} className="ml-2" />
              </Link>
            </div>
          </div>

          {/* Editor window — theme-aware: dark editor in dark mode, light editor
              in light mode. Chrome, accents, and syntax colors all flip via
              tokens (see code-display.tsx for the light/dark Prism themes). */}
          <div
            className="border-border bg-card overflow-hidden rounded-2xl border"
            style={{ boxShadow: "0 8px 30px -10px color-mix(in srgb, black 14%, transparent)" }}
          >
            {/* Window chrome: traffic dots, filename, copy */}
            <div
              className="border-border flex items-center gap-3 border-b px-4 py-3"
              style={{
                background: "color-mix(in srgb, var(--foreground) 3%, var(--card))",
                boxShadow: "inset 0 1px 0 0 color-mix(in srgb, var(--foreground) 6%, transparent)",
              }}
            >
              <div className="flex shrink-0 items-center gap-1.5" aria-hidden="true">
                <span className="bg-foreground/20 h-2.5 w-2.5 rounded-full" />
                <span className="bg-foreground/20 h-2.5 w-2.5 rounded-full" />
                <span className="bg-foreground/20 h-2.5 w-2.5 rounded-full" />
              </div>
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={activeTab}
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -3 }}
                  transition={{ duration: 0.18 }}
                  className="text-muted-foreground min-w-0 flex-1 truncate text-center font-mono text-xs"
                >
                  {FILENAMES[activeTab]}
                </motion.span>
              </AnimatePresence>
              <button
                onClick={handleCopy}
                aria-label={copied ? "Copied!" : "Copy code"}
                className={cn(
                  "flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-all",
                  copied
                    ? "text-brand-green border-brand-green/35 bg-brand-green/10"
                    : "border-border/70 text-muted-foreground hover:text-foreground hover:bg-muted bg-foreground/3"
                )}
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>

            {/* Language tabs — gliding underline, swipe hint on mobile */}
            <div className="border-border relative border-b">
              <div className="scrollbar-hide overflow-x-auto">
                <div
                  role="tablist"
                  aria-label="Programming languages"
                  className="flex items-center gap-0.5 px-2 pr-10 sm:pr-2"
                  onKeyDown={handleTabKeyDown}
                >
                  {languages.map((lang, i) => {
                    const isActive = activeTab === lang.id;
                    return (
                      <button
                        key={lang.id}
                        ref={(el) => {
                          tabRefs.current[i] = el;
                        }}
                        role="tab"
                        id={`api-tab-${lang.id}`}
                        aria-selected={isActive}
                        aria-controls={`api-panel-${lang.id}`}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => setActiveTab(lang.id)}
                        className={cn(
                          "relative flex shrink-0 items-center gap-2 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-colors duration-200 outline-none",
                          "focus-visible:ring-2 focus-visible:ring-[rgba(91,140,255,0.45)] focus-visible:ring-inset",
                          isActive
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        <span
                          className="flex h-4 items-center transition-all duration-200"
                          style={{
                            filter: isActive ? "none" : "grayscale(1)",
                            opacity: isActive ? 1 : 0.45,
                          }}
                          aria-hidden="true"
                        >
                          <Image
                            src={lang.iconSrc}
                            alt=""
                            width={16}
                            height={16}
                            style={{ height: "16px", width: "auto" }}
                            className="max-w-none object-contain"
                          />
                        </span>
                        {lang.label}
                        {isActive && (
                          <motion.span
                            layoutId="api-tab-underline"
                            aria-hidden="true"
                            className="absolute inset-x-2 bottom-0 h-[2px] rounded-full"
                            style={{ backgroundColor: "var(--brand-green)" }}
                            transition={
                              prefersReducedMotion
                                ? { duration: 0 }
                                : { type: "spring", stiffness: 420, damping: 36 }
                            }
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1 pl-8 sm:hidden"
                style={{ background: "linear-gradient(to right, transparent, var(--card) 65%)" }}
              >
                <ChevronRight
                  size={14}
                  className="text-muted-foreground motion-safe:animate-pulse"
                />
              </div>
            </div>

            {/* Code — no wrapping; scrolls (swipes) horizontally on mobile */}
            <div
              id={`api-panel-${activeTab}`}
              role="tabpanel"
              aria-labelledby={`api-tab-${activeTab}`}
              className="relative h-[340px] overflow-auto overscroll-x-contain sm:h-[430px]"
            >
              {/* The highlighter pulls ~500KB of Prism. The panel below the fold
                  doesn't need it during the hero's paint, and the wrapper above
                  already reserves this block's height, so gating the mount on
                  proximity keeps that chunk off the critical path with no shift. */}
              <LazyMount>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={prefersReducedMotion ? false : "hidden"}
                    animate="visible"
                    exit="exit"
                    variants={codeVariants}
                    className="w-fit min-w-full"
                  >
                    <CodeDisplay activeTab={activeTab} code={codeSnippets[activeTab]} />
                  </motion.div>
                </AnimatePresence>
              </LazyMount>
            </div>

            {/* Status bar */}
            <div className="border-border text-muted-foreground flex items-center justify-between border-t px-4 py-2 font-mono text-[11px]">
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: "var(--brand-green)" }}
                  aria-hidden="true"
                />
                gate.proxidize.com:7777
              </span>
              <span>
                {lineCount} lines · {languages.find((l) => l.id === activeTab)?.label}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
